import Papa from "papaparse";
import * as XLSX from "xlsx";

// UX-P04 SCR-P04-10/11/12 — every bit of this file runs IN THE BROWSER. Raw file contents are
// never sent to the server (only file name + row count, plus the mapping/dedupe DECISIONS the
// user makes) — matches the backend's own documented privacy posture in migration_engine.py.

export const CANONICAL_FIELDS = [
  { key: "name", label: "Name", required: true },
  { key: "phone", label: "Phone", required: true },
  { key: "email", label: "Email", required: false },
  { key: "plan_ref", label: "Plan", required: false },
  { key: "start", label: "Start date", required: false },
  { key: "remaining_days", label: "Remaining days", required: false },
  { key: "remaining_sessions", label: "Remaining sessions", required: false },
  { key: "freezes_taken", label: "Freezes taken", required: false },
  { key: "advance_amount", label: "Advance / wallet credit (₹)", required: false },
  { key: "dues_amount", label: "Dues outstanding (₹)", required: false },
];

// Parses one File into { name, headers, rows, error }. CSV via PapaParse, Excel via SheetJS.
export function parseFile(file) {
  return new Promise((resolve) => {
    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    if (isExcel) {
      const reader = new FileReader();
      reader.onerror = () => resolve({ name: file.name, headers: [], rows: [], error: "Could not read this file." });
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: "array" });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
          const headers = json.length ? Object.keys(json[0]) : [];
          resolve({ name: file.name, headers, rows: json, error: json.length ? null : "No rows found in this sheet." });
        } catch {
          resolve({ name: file.name, headers: [], rows: [], error: "This file doesn't look like a valid Excel workbook." });
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      Papa.parse(file, {
        header: true, skipEmptyLines: true,
        complete: (result) => {
          const headers = result.meta.fields || [];
          if (result.errors?.length && !result.data.length) {
            resolve({ name: file.name, headers: [], rows: [], error: "This file doesn't look like a valid CSV." });
          } else {
            resolve({ name: file.name, headers, rows: result.data, error: headers.length ? null : "No columns found in this file." });
          }
        },
        error: () => resolve({ name: file.name, headers: [], rows: [], error: "Could not read this file." }),
      });
    }
  });
}

export function normalizePhone(v) {
  return (v || "").toString().replace(/\D/g, "").slice(-10);
}

function toISODate(v) {
  if (!v) return null;
  const s = v.toString().trim();
  const isoMatch = /^\d{4}-\d{2}-\d{2}/.exec(s);
  if (isoMatch) return isoMatch[0];
  const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(s);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  return null;
}

function toInt(v) {
  const n = parseInt(String(v).replace(/[^\d-]/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}

function toPaise(v) {
  const n = parseFloat(String(v).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
}

// Maps one raw parsed row (plain object keyed by original header) into the backend's
// MigrationSourceRow shape, given a { header: canonicalField } mapping.
export function buildMigrationRow(rawRow, mapping, fileName, index) {
  const row = { row_key: `${fileName}::${index}`, source_file: fileName, freezes_taken: 0, advance_amount: 0, dues_amount: 0, history: [] };
  for (const [header, field] of Object.entries(mapping)) {
    if (!field || rawRow[header] === undefined) continue;
    const raw = rawRow[header];
    if (field === "start") row.start = toISODate(raw);
    else if (field === "remaining_days" || field === "remaining_sessions" || field === "freezes_taken") row[field] = toInt(raw);
    else if (field === "advance_amount" || field === "dues_amount") row[field] = toPaise(raw);
    else if (field === "name" || field === "email" || field === "plan_ref") row[field] = raw ? String(raw).trim() : null;
    else if (field === "phone") row.phone = normalizePhone(raw) || null;
  }
  return row;
}

// SCR-P04-12 — groups rows across ALL files by normalized phone; only groups with 2+ rows are
// real duplicates. Rows with no phone at all are never grouped (they're already "flagged" by
// the missing-required-field rule, not a dedupe concern).
export function findDuplicateGroups(rows) {
  const byPhone = {};
  for (const row of rows) {
    if (!row.phone) continue;
    (byPhone[row.phone] = byPhone[row.phone] || []).push(row);
  }
  return Object.entries(byPhone).filter(([, group]) => group.length > 1).map(([phone, group]) => ({ key: phone, rows: group }));
}

// "Merge" action (SCR-P04-12 default): field-by-field, the first row that HAS a value wins;
// later rows only fill gaps. Produces exactly one row.
export function mergeRows(rows) {
  const merged = { ...rows[0] };
  for (const field of ["name", "phone", "email", "plan_ref", "start", "remaining_days", "remaining_sessions", "freezes_taken", "advance_amount", "dues_amount"]) {
    const hasValue = (v) => v !== null && v !== undefined && v !== "" && v !== 0;
    if (!hasValue(merged[field])) {
      const donor = rows.find((r) => hasValue(r[field]));
      if (donor) merged[field] = donor[field];
    }
  }
  return merged;
}

// SCR-P04-12 - applies field overrides (bulk-fill + inline preview fixes) then every duplicate
// group's chosen action, producing the exact row list Preview/Commit will receive.
export function computeFinalRows(mappedRows, dedupeDecisions, fieldOverrides) {
  const withOverrides = mappedRows.map((r) => ({ ...r, ...(fieldOverrides[r.row_key] || {}) }));
  const groups = findDuplicateGroups(withOverrides);
  const rowKeysToDrop = new Set();
  const extraRows = [];

  for (const group of groups) {
    const decision = dedupeDecisions[group.key] || { action: "merge", primaryRowKey: group.rows[0].row_key };
    if (decision.action === "merge") {
      const ordered = [group.rows.find((r) => r.row_key === decision.primaryRowKey) || group.rows[0], ...group.rows.filter((r) => r.row_key !== decision.primaryRowKey)];
      extraRows.push(mergeRows(ordered));
      group.rows.forEach((r) => rowKeysToDrop.add(r.row_key));
    } else if (decision.action === "keep_primary") {
      const primary = group.rows.find((r) => r.row_key === decision.primaryRowKey) || group.rows[0];
      group.rows.forEach((r) => { if (r.row_key !== primary.row_key) rowKeysToDrop.add(r.row_key); });
    }
    // send_both: nothing dropped - both rows travel through as-is.
  }

  return [...withOverrides.filter((r) => !rowKeysToDrop.has(r.row_key)), ...extraRows];
}
